import { useEffect, useState } from "react";
import { MEDDRA_MIN_QUERY, searchMedDraTerms, type MedDraMatch } from "../services/meddraLookupService";

/**
 * What the MedDRA box has to show right now.
 *
 * <p>`empty` and `failed` are deliberately different: a coding control that shows a failed lookup
 * as "no matches" invites a reviewer to conclude a term does not exist when nobody asked.
 */
export type MedDraSearchStatus = "idle" | "searching" | "matches" | "empty" | "failed";

export interface MedDraSearch {
  status: MedDraSearchStatus;
  matches: MedDraMatch[];
}

/** Long enough that a reviewer has stopped typing, short enough not to feel laggy. */
const SETTLE_MS = 250;

/**
 * Searches MedDRA as the reviewer types, and owns the lifecycle that needs owning.
 *
 * <p>A hook rather than state in the control because each keystroke starts a request the next one
 * has to abandon: without the abort, a slow early answer arrives after a fast later one and the
 * list shows matches for a term nobody is looking at any more.
 */
export function useMedDraSearch(query: string, enabled: boolean): MedDraSearch {
  const [search, setSearch] = useState<MedDraSearch>({ status: "idle", matches: [] });

  useEffect(() => {
    const term = query.trim();
    if (!enabled || term.length < MEDDRA_MIN_QUERY) {
      setSearch({ status: "idle", matches: [] });
      return;
    }
    const controller = new AbortController();
    setSearch((current) => ({ status: "searching", matches: current.matches }));
    const timer = setTimeout(() => {
      searchMedDraTerms(term, controller.signal)
        .then((matches) => setSearch({ status: matches.length ? "matches" : "empty", matches }))
        .catch(() => {
          if (controller.signal.aborted) return;
          setSearch({ status: "failed", matches: [] });
        });
    }, SETTLE_MS);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, enabled]);

  return search;
}
